// Path matching for the SPA. A GitHub pull request lives under `/gh` followed
// by its path on github.com, so swapping `github.com` for `<host>/gh` in a PR
// URL lands on the same PR (see docs/design.md, "URLs"). The prefix names the
// forge; the rest of the address space is the app's own.

export type Route =
  | { name: 'dashboard' }
  | { name: 'pulls'; owner: string; repo: string }
  | { name: 'overview'; owner: string; repo: string; number: number }
  | { name: 'files'; owner: string; repo: string; number: number }
  | { name: 'commits'; owner: string; repo: string; number: number }
  /** The Review view: the viewer's unsent drafts, sending them, and everything said in review. */
  | { name: 'review'; owner: string; repo: string; number: number }
  /** The signed-in user's own settings. */
  | { name: 'settings' }
  | { name: 'org-settings'; org: string }
  /** Feedback reports, for the deployment's operators. */
  | { name: 'feedback' }
  | { name: 'feedback-report'; id: number }
  | { name: 'not-found'; path: string };

export type PrRoute = Extract<Route, { number: number }>;

/** Server-owned prefixes the SPA never handles. */
export const RESERVED_PREFIXES = ['/api/', '/auth/', '/webhooks/', '/assets/'];

/** Server-rendered pages (exact path or below it); links to them load the full page. */
export const SERVER_PAGES = ['/setup'];

function isServerPath(pathname: string): boolean {
  return (
    RESERVED_PREFIXES.some((p) => pathname.startsWith(p)) ||
    SERVER_PAGES.some((p) => pathname === p || pathname.startsWith(p + '/'))
  );
}

/** The prefix of every route that addresses a GitHub repository. */
export const GH = '/gh';

const NAME = '[A-Za-z0-9_.-]+';
const PR = new RegExp(`^${GH}/(${NAME})/(${NAME})/pull/(\\d+)(?:/(files|changes|commits|review|drafts|publish))?/?$`);
const PULLS = new RegExp(`^${GH}/(${NAME})/(${NAME})/pulls/?$`);
const SETTINGS = /^\/settings\/?$/;
const ORG = new RegExp(`^/settings/orgs/(${NAME})/?$`);
const FEEDBACK = /^\/feedback(?:\/(\d+))?\/?$/;

export function matchRoute(pathname: string): Route {
  let path: string;
  try {
    path = decodeURI(pathname);
  } catch {
    path = pathname;
  }
  if (path === '/' || path === '') return { name: 'dashboard' };
  let m = PR.exec(path);
  if (m) {
    const [, owner, repo, n, tab] = m;
    const number = Number(n);
    if (!Number.isSafeInteger(number) || number <= 0) return { name: 'not-found', path };
    // `/drafts` and `/publish` are other URLs of the Review view (see `formerAlias`).
    const name = tab === 'files' || tab === 'changes' ? 'files' : tab === 'commits' ? 'commits' : tab === 'review' || tab === 'drafts' || tab === 'publish' ? 'review' : 'overview';
    return { name, owner, repo, number };
  }
  m = PULLS.exec(path);
  if (m) return { name: 'pulls', owner: m[1], repo: m[2] };
  if (SETTINGS.test(path)) return { name: 'settings' };
  m = ORG.exec(path);
  if (m) return { name: 'org-settings', org: m[1] };
  m = FEEDBACK.exec(path);
  if (m) {
    if (m[1] === undefined) return { name: 'feedback' };
    const id = Number(m[1]);
    if (Number.isSafeInteger(id) && id > 0) return { name: 'feedback-report', id };
  }
  return { name: 'not-found', path };
}

const SHA = '[0-9a-fA-F]{7,40}';
const PR_ALIAS = new RegExp(
  `^${GH}/(${NAME})/(${NAME})/pull/(\\d+)/(?:(?:commits|files|changes)/(${SHA})|(?:files|changes)/(${SHA})\\.\\.\\.?(${SHA})|(checks))/?$`,
);

/** Suffix of a `from` value naming the parent of a commit (`<sha>^`), as git writes it. */
export const PARENT = '^';

/**
 * The changelist URL for one of GitHub's other pull request URLs, which a
 * host swap may bring here, or null when `pathname` is not one:
 * `/pull/N/commits/<sha>` and `/pull/N/files/<sha>` (one commit: the Files
 * view from its parent, `from=<sha>^`, to it), `/pull/N/files/<a>..<b>` (a
 * commit range: `from=<a>&to=<b>`), and `/pull/N/checks` (the Overview's
 * Checks card, `focus=checks`). `search` is the URL's query (kept, with these
 * parameters set).
 */
export function githubAlias(pathname: string, search = ''): string | null {
  let path: string;
  try {
    path = decodeURI(pathname);
  } catch {
    path = pathname;
  }
  const m = PR_ALIAS.exec(path);
  if (!m) return null;
  const [, owner, repo, n, one, a, b, checks] = m;
  const number = Number(n);
  if (!Number.isSafeInteger(number) || number <= 0) return null;
  const q = new URLSearchParams(search);
  let target: string;
  if (checks) {
    target = prPath(owner, repo, number);
    q.set('focus', 'checks'); // headerTargets' FOCUS_PARAM
  } else {
    target = prPath(owner, repo, number, 'files');
    q.set('from', one ? one.toLowerCase() + PARENT : a.toLowerCase());
    q.set('to', (one ?? b).toLowerCase());
  }
  const qs = q.toString();
  return target + (qs ? '?' + qs : '');
}

const FORMER = new RegExp(`^${GH}/(${NAME})/(${NAME})/pull/(\\d+)/(?:drafts|publish)/?$`);

/**
 * The URL of the Review view for a URL of one of the two views it
 * replaced (`/pull/N/drafts`, `/pull/N/publish`), or null when `pathname`
 * is neither: a link or bookmark to either lands on the same pull request's
 * Review view, with its query kept, and the address bar says so.
 */
export function formerAlias(pathname: string, search = ''): string | null {
  let path: string;
  try {
    path = decodeURI(pathname);
  } catch {
    path = pathname;
  }
  const m = FORMER.exec(path);
  if (!m) return null;
  const number = Number(m[3]);
  if (!Number.isSafeInteger(number) || number <= 0) return null;
  return prPath(m[1], m[2], number, 'review') + search;
}

/** A pull request's views, named as their routes name them (`commits` is Snapshots). */
export type PrTab = 'overview' | 'files' | 'commits' | 'review';

export function prPath(owner: string, repo: string, number: number, tab: '' | PrTab = ''): string {
  return `${GH}/${owner}/${repo}/pull/${number}${tab && tab !== 'overview' ? '/' + tab : ''}`;
}

/** True when an anchor click should be handled by the SPA router. */
export function isRoutableHref(href: string, origin: string): boolean {
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return false;
  }
  if (url.origin !== origin) return false;
  return !isServerPath(url.pathname);
}

/** Same-origin path check for `return_to` style parameters. */
export function isSafeReturnPath(p: string): boolean {
  return p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/\\');
}
